export const BUBBLE_TITLE_CHAR_RATIO = 0.58; // avg glyph width / font-size
export const BUBBLE_TITLE_FALLBACK = "Unavailable account name";

/** Share of the canvas the bubbles cover between them (area-proportional sizing). */
export const BUBBLE_FILL_RATIO = 0.42;
/** Smallest radius drawn, so a tiny balance still shows as a tappable bubble. */
export const BUBBLE_MIN_RADIUS = 16;
/** Kept clear between the bubbles and the canvas edge / divider. */
export const BUBBLE_EDGE_PAD = 4;
/** Space kept between neighbouring bubbles. */
export const BUBBLE_GAP = 2;
/** Smallest bubble, relative to the largest on its side, that still joins the row on the floor. */
export const BUBBLE_ROW_MIN_RATIO = 0.55;
/** Below this radius a bubble only shows its (trimmed) name; the rest is in the tooltip. */
export const BUBBLE_FULL_LABEL_MIN_RADIUS = 40;

export interface BubbleSizing {
  /** Radius per amount, in input order. */
  radii: number[];
  /** y of the line between money-in (above) and money-out (below); null with only one sign. */
  divider: number | null;
}

/**
 * Sizes the bubbles so their area follows the amount (|amount|), covering BUBBLE_FILL_RATIO of
 * the canvas (times `scale`), then places the divider by the money-in / money-out area split.
 * Everything is scaled down when the largest bubble on a side wouldn't fit its side of the
 * divider. No bubble goes under BUBBLE_MIN_RADIUS.
 */
export function computeBubbleSizing(
  amounts: number[],
  width: number,
  height: number,
  scale = 1,
): BubbleSizing {
  const total = amounts.reduce((sum, amount) => sum + Math.abs(amount), 0);
  if (amounts.length === 0 || total === 0) {
    return { radii: amounts.map(() => BUBBLE_MIN_RADIUS), divider: null };
  }
  const k = scale * Math.sqrt((BUBBLE_FILL_RATIO * width * height) / (Math.PI * total));
  const raw = amounts.map((amount) => k * Math.sqrt(Math.abs(amount)));

  const maxOf = (radii: number[], positive: boolean) =>
    Math.max(0, ...radii.filter((_, i) => amounts[i] >= 0 === positive));
  const both = amounts.some((a) => a >= 0) && amounts.some((a) => a < 0);
  // Each side keeps a pad at its canvas edge and at the divider: 4 pads with both sides, 2 without.
  const usableHeight = height - (both ? 4 : 2) * BUBBLE_EDGE_PAD;
  const neededHeight = both ? 2 * (maxOf(raw, true) + maxOf(raw, false)) : 2 * Math.max(...raw);
  const shrink = Math.min(
    1,
    usableHeight / neededHeight,
    (width - 2 * BUBBLE_EDGE_PAD) / (2 * Math.max(...raw)),
  );
  // The minimum goes on after the shrink, so the smallest bubbles stay tappable.
  const radii = raw.map((r) => Math.max(BUBBLE_MIN_RADIUS, r * shrink));

  if (!both) {
    return { radii, divider: null };
  }
  const area = (positive: boolean) =>
    radii.reduce((sum, r, i) => (amounts[i] >= 0 === positive ? sum + r * r : sum), 0);
  const split = (height * area(true)) / (area(true) + area(false));
  const lowest = 2 * maxOf(radii, true) + 2 * BUBBLE_EDGE_PAD;
  const highest = height - 2 * maxOf(radii, false) - 2 * BUBBLE_EDGE_PAD;
  return { radii, divider: Math.min(highest, Math.max(lowest, split)) };
}

export interface BubbleLayout extends BubbleSizing {
  x: number[];
  y: number[];
}

/** How much smaller each retry makes the bubbles when they don't all fit. */
const BUBBLE_RETRY_SCALE = 0.9;
const BUBBLE_MAX_RETRIES = 10;

/**
 * Sizes and places the bubbles (computeBubbleSizing, then layoutBubbles). When a crowded side
 * leaves a bubble nowhere to go, everything is tried again smaller, so they don't overlap.
 */
export function computeBubbleLayout(
  amounts: number[],
  width: number,
  height: number,
): BubbleLayout {
  let scale = 1;
  for (let attempt = 0; ; attempt++) {
    const sizing = computeBubbleSizing(amounts, width, height, scale);
    const spots = layoutBubbles(sizing.radii, amounts, width, height, sizing.divider);
    if (spots) {
      return { ...sizing, ...spots };
    }
    if (attempt === BUBBLE_MAX_RETRIES) {
      // Still crowded after every retry (more minimum-size bubbles than fit): one row per side,
      // squeezed across, so they overlap but stay on their own side of the divider.
      return { ...sizing, ...rowsOnFloor(sizing.radii, amounts, width, height, sizing.divider) };
    }
    scale *= BUBBLE_RETRY_SCALE;
  }
}

/** Last-resort layout: each side's bubbles in one row on its floor, spread evenly across. */
function rowsOnFloor(
  radii: number[],
  amounts: number[],
  width: number,
  height: number,
  divider: number | null,
): { x: number[]; y: number[] } {
  const x = radii.map(() => width / 2);
  const y = radii.map(() => height / 2);
  for (const positive of [true, false]) {
    const floor = positive
      ? (divider ?? height) - BUBBLE_EDGE_PAD
      : (divider ?? 0) + BUBBLE_EDGE_PAD;
    const side = radii.map((_, i) => i).filter((i) => amounts[i] >= 0 === positive);
    side.forEach((i, n) => {
      x[i] = (width * (n + 1)) / (side.length + 1);
      y[i] = positive ? floor - radii[i] : floor + radii[i];
    });
  }
  return { x, y };
}

interface Placed {
  x: number;
  /** Height of the centre above the floor. */
  h: number;
  r: number;
}

/**
 * Where each bubble sits, as in the design: packed against the divider. On each side the largest
 * bubbles form one row on the floor (the divider, or the canvas edge when every balance has the
 * same sign): biggest first while they fit across and are at least BUBBLE_ROW_MIN_RATIO of the
 * biggest, laid out biggest-in-the-middle and centred. Each remaining bubble, largest first, goes
 * to the spot nearest the floor where it rests on the floor or on bubbles already placed, without
 * overlapping any and without a taller bubble hanging over it, so the small ones nestle into the
 * dips and shoulders of the big ones rather than rolling in under them. Null when a bubble has
 * nowhere to go without overlapping another.
 */
export function layoutBubbles(
  radii: number[],
  amounts: number[],
  width: number,
  height: number,
  divider: number | null,
): { x: number[]; y: number[] } | null {
  const x = radii.map(() => width / 2);
  const y = radii.map(() => height / 2);
  for (const positive of [true, false]) {
    // Work in height above this side's floor, then map back to canvas y.
    const floor = positive
      ? (divider ?? height) - BUBBLE_EDGE_PAD
      : (divider ?? 0) + BUBBLE_EDGE_PAD;
    const room = positive ? floor - BUBBLE_EDGE_PAD : height - BUBBLE_EDGE_PAD - floor;
    const toY = (h: number) => (positive ? floor - h : floor + h);

    const side = radii
      .map((r, i) => ({ r, i }))
      .filter(({ i }) => amounts[i] >= 0 === positive)
      .sort((p, q) => q.r - p.r);
    if (side.length === 0) {
      continue;
    }

    // The row: largest first, while they fit and are big enough.
    const usable = width - 2 * BUBBLE_EDGE_PAD;
    let rowEnd = 1;
    let rowWidth = 2 * side[0].r;
    while (
      rowEnd < side.length &&
      side[rowEnd].r >= BUBBLE_ROW_MIN_RATIO * side[0].r &&
      rowWidth + BUBBLE_GAP + 2 * side[rowEnd].r <= usable
    ) {
      rowWidth += BUBBLE_GAP + 2 * side[rowEnd].r;
      rowEnd++;
    }
    // Biggest in the middle, the rest alternating right / left of it.
    const row = side.slice(0, rowEnd);
    const ordered = row.reduce<typeof row>(
      (line, item, n) => (n % 2 === 1 ? [...line, item] : [item, ...line]),
      [],
    );
    const placed: Placed[] = [];
    let left = (width - rowWidth) / 2;
    for (const { r, i } of ordered) {
      placed.push({ x: left + r, h: r, r });
      x[i] = left + r;
      y[i] = toY(r);
      left += 2 * r + BUBBLE_GAP;
    }

    for (const { r, i } of side.slice(rowEnd)) {
      const spot = restingSpot(r, placed, width, room);
      if (!spot) {
        return null;
      }
      placed.push({ ...spot, r });
      x[i] = spot.x;
      y[i] = toY(spot.h);
    }
  }
  return { x, y };
}

/** The lowest valid spot for a bubble of radius r, resting on the floor or on placed bubbles. */
function restingSpot(
  r: number,
  placed: Placed[],
  width: number,
  room: number,
): Omit<Placed, "r"> | undefined {
  const candidates: Omit<Placed, "r">[] = [];
  for (const p of placed) {
    // On the floor, against p.
    const d = p.r + r + BUBBLE_GAP;
    const dh = p.h - r;
    if (d >= Math.abs(dh)) {
      const dx = Math.sqrt(d * d - dh * dh);
      candidates.push({ x: p.x - dx, h: r }, { x: p.x + dx, h: r });
    }
    // Resting on p and another placed bubble q.
    for (const q of placed) {
      if (q !== p) {
        candidates.push(...tangentToBoth(p, q, r));
      }
    }
    // On top of p.
    candidates.push({ x: p.x, h: p.h + d });
  }

  const eps = 1e-6;
  const fits = (c: Omit<Placed, "r">) =>
    c.x - r >= BUBBLE_EDGE_PAD - eps &&
    c.x + r <= width - BUBBLE_EDGE_PAD + eps &&
    c.h - r >= -eps &&
    c.h + r <= room + eps &&
    placed.every((p) => Math.hypot(p.x - c.x, p.h - c.h) >= p.r + r + BUBBLE_GAP - eps);
  // A taller bubble reaching over the spot, to within `reach` of its centre line.
  const over = (c: Omit<Placed, "r">, p: Placed, reach: number) =>
    p.h > c.h && Math.abs(p.x - c.x) < p.r + reach - eps;
  // Not under a taller bubble's belly (its centre within that bubble's width), and not down the
  // crack between two touching ones, each reaching over part of it from either side.
  const inTheOpen = (c: Omit<Placed, "r">) =>
    placed.every((p) => !over(c, p, 0)) &&
    !(
      placed.some((p) => p.x < c.x && over(c, p, r)) &&
      placed.some((p) => p.x > c.x && over(c, p, r))
    );
  const centre = width / 2;
  // Lowest first (to the pixel), then nearest the middle.
  const lowest = (spots: Omit<Placed, "r">[]) =>
    spots.sort(
      (a, b) =>
        Math.round(a.h) - Math.round(b.h) || Math.abs(a.x - centre) - Math.abs(b.x - centre),
    )[0];
  const open = candidates.filter(fits);
  // A side the big bubbles fill may leave no such spot; then take any that fits.
  return lowest(open.filter(inTheOpen)) ?? lowest(open);
}

/** Centres where a bubble of radius r touches both p and q (with the gap). */
function tangentToBoth(p: Placed, q: Placed, r: number): Omit<Placed, "r">[] {
  const da = p.r + r + BUBBLE_GAP;
  const db = q.r + r + BUBBLE_GAP;
  const dx = q.x - p.x;
  const dh = q.h - p.h;
  const d = Math.hypot(dx, dh);
  if (d === 0 || d > da + db || d < Math.abs(da - db)) {
    return [];
  }
  const a = (da * da - db * db + d * d) / (2 * d);
  const off = Math.sqrt(Math.max(0, da * da - a * a));
  const mx = p.x + (a * dx) / d;
  const mh = p.h + (a * dh) / d;
  return [
    { x: mx - (off * dh) / d, h: mh + (off * dx) / d },
    { x: mx + (off * dh) / d, h: mh - (off * dx) / d },
  ];
}

/** How far a bubble drifts in towards the divider as it appears. */
export const BUBBLE_DRIFT = 40;

/**
 * Where a bubble's entrance drift starts: pulled in towards the middle and BUBBLE_DRIFT further
 * from the divider (above it for money-in, below for money-out), so each one settles onto the
 * divider like it was laid out by gravity.
 */
export function bubbleDriftStart(
  x: number,
  y: number,
  amount: number,
  width: number,
): { x: number; y: number } {
  const centre = width / 2;
  return {
    x: centre + (x - centre) * 0.6,
    y: amount >= 0 ? y - BUBBLE_DRIFT : y + BUBBLE_DRIFT,
  };
}

/** Each bubble's share of all the balances shown, by absolute amount, e.g. "49%" or "<1%". */
export function bubbleShare(amount: number, amounts: number[]): string {
  const total = amounts.reduce((sum, a) => sum + Math.abs(a), 0);
  if (total === 0) {
    return "0%";
  }
  const percent = (Math.abs(amount) / total) * 100;
  return percent > 0 && percent < 1 ? "<1%" : `${Math.round(percent)}%`;
}

export interface BubbleLabelLine {
  kind: "share" | "name" | "amount";
  text: string;
  fontSize: number;
  /** Baseline, relative to the bubble centre. */
  y: number;
}

export interface BubbleLabel {
  /** Where the lines start (`start`: the block's left edge) or centre, relative to the bubble centre. */
  x: number;
  anchor: "start" | "middle";
  lines: BubbleLabelLine[];
}

// Code points that render about 1em wide (others about BUBBLE_TITLE_CHAR_RATIO em): hangul jamo,
// emoji-style symbols (misc technical, misc symbols / dingbats, arrows and stars), CJK / kana /
// hangul syllables, compatibility and full-width forms, the emoji planes and the supplementary
// CJK planes. Over-counting a narrow symbol only trims a name early; under-counting would let it
// overflow.
const FULL_WIDTH: [number, number][] = [
  [0x1100, 0x115f],
  [0x2300, 0x23ff],
  [0x2600, 0x27bf],
  [0x2b00, 0x2bff],
  [0x2e80, 0xa4cf],
  [0xac00, 0xd7a3],
  [0xf900, 0xfaff],
  [0xfe30, 0xfe4f],
  [0xff00, 0xff60],
  [0xffe0, 0xffe6],
  [0x1f000, 0x1faff],
  [0x20000, 0x3ffff],
];
// Code points that only modify the one before: combining marks, the keycap, variation selectors,
// skin tones and tag characters (subdivision flags), plus the joiner itself.
const ATTACHED: [number, number][] = [
  [0x0300, 0x036f],
  [0x200d, 0x200d],
  [0x20e3, 0x20e3],
  [0xfe00, 0xfe0f],
  [0x1f3fb, 0x1f3ff],
  [0xe0020, 0xe007f],
];
const ZERO_WIDTH_JOINER = 0x200d;
const EMOJI_PRESENTATION = 0xfe0f;
const REGIONAL_INDICATOR: [number, number] = [0x1f1e6, 0x1f1ff];
const inRange = (code: number, [from, to]: [number, number]) => code >= from && code <= to;

/**
 * `text` split into what reads as one character each: a base plus whatever attaches to it, the
 * parts of a joined emoji, a flag's two regional indicators. Trimming by these never cuts an
 * emoji or accented letter apart.
 */
const clusters = (text: string): number[][] => {
  const result: number[][] = [];
  for (const char of Array.from(text)) {
    const code = char.codePointAt(0) ?? 0;
    const last = result[result.length - 1];
    const pairsFlag =
      last?.length === 1 &&
      inRange(last[0], REGIONAL_INDICATOR) &&
      inRange(code, REGIONAL_INDICATOR);
    const joins =
      last &&
      (ATTACHED.some((range) => inRange(code, range)) ||
        last[last.length - 1] === ZERO_WIDTH_JOINER ||
        pairsFlag);
    if (joins) {
      last.push(code);
    } else {
      result.push([code]);
    }
  }
  return result;
};
// A cluster is as wide as its base, or a full em when it asks for emoji presentation.
const clusterWidth = (cluster: number[]) =>
  cluster.includes(EMOJI_PRESENTATION) || FULL_WIDTH.some((range) => inRange(cluster[0], range))
    ? 1
    : BUBBLE_TITLE_CHAR_RATIO;
// Semibold (the share and amount lines) runs about this much wider than regular.
const BOLD_WIDTH = 1.08;
/** Estimated rendered width of `text` at `fontSize` (`bold`: semibold). */
export const textWidth = (text: string, fontSize: number, bold = false) =>
  clusters(text).reduce((sum, cluster) => sum + clusterWidth(cluster), 0) *
  fontSize *
  (bold ? BOLD_WIDTH : 1);

/** `text`, ellipsis-trimmed (keeping at least one character) to fit `maxWidth`. */
const trimToWidth = (text: string, fontSize: number, maxWidth: number) => {
  const parts = clusters(text);
  const widths = parts.map((part) => clusterWidth(part) * fontSize);
  if (widths.reduce((sum, w) => sum + w, 0) <= maxWidth) {
    return text;
  }
  const budget = maxWidth - textWidth("…", fontSize);
  let kept = 1;
  let used = widths[0];
  while (kept < parts.length && used + widths[kept] <= budget) {
    used += widths[kept];
    kept++;
  }
  return parts.slice(0, kept).reduce((s, part) => s + String.fromCodePoint(...part), "") + "…";
};

/**
 * Label for a bubble, as in the design: a left-aligned block of share (large), name (small) and
 * amount (medium), centred in the bubble. A bubble under BUBBLE_FULL_LABEL_MIN_RADIUS only gets
 * its name. Sizes follow the radius; the name is ellipsis-trimmed to fit the block.
 */
export function bubbleLabel(
  radius: number,
  name: string | null | undefined,
  share: string,
  amount: string,
): BubbleLabel {
  const resolved = name || BUBBLE_TITLE_FALLBACK;
  // Width a label line can use across the bubble's middle.
  const maxWidth = radius * 2 * 0.72;

  if (radius < BUBBLE_FULL_LABEL_MIN_RADIUS) {
    const fontSize = Math.max(6, Math.min(11, radius * 0.4));
    const text = trimToWidth(resolved, fontSize, radius * 2 * 0.8);
    // One line, so the renderer can centre it exactly.
    return {
      x: 0,
      anchor: "middle",
      lines: [{ kind: "name", text, fontSize, y: fontSize * 0.35 }],
    };
  }

  const shareFont = Math.min(28, radius * 0.28);
  const nameFont = Math.max(8, Math.min(11, radius * 0.17));
  const amountFont = Math.min(18, radius * 0.19);
  const nameText = trimToWidth(resolved, nameFont, maxWidth);
  const gap = 1.3;
  const height = shareFont + nameFont * gap + amountFont * gap;
  const top = -height / 2;
  const lines: BubbleLabelLine[] = [
    { kind: "share", text: share, fontSize: shareFont, y: top + shareFont * 0.85 },
    { kind: "name", text: nameText, fontSize: nameFont, y: top + shareFont + nameFont * gap },
    {
      kind: "amount",
      text: amount,
      fontSize: amountFont,
      y: top + shareFont + nameFont * gap + amountFont * gap * 0.95,
    },
  ];
  const blockWidth = Math.min(
    maxWidth,
    Math.max(...lines.map((l) => textWidth(l.text, l.fontSize, l.kind !== "name"))),
  );
  return { x: -blockWidth / 2, anchor: "start", lines };
}

/**
 * Bubble fill. Light mode uses the glassy gradients built in createGlassyGradients; dark mode
 * uses flat tokens: today-accent teal for a positive balance, the page surface (near-black) for
 * a negative one, which leaves the red to the amount text (--color-money-out-text).
 */
export function bubbleFill(amount: number, dark: boolean): string {
  if (dark) {
    return amount >= 0 ? "var(--color-accent-today-bg)" : "var(--color-surface-page)";
  }
  return amount >= 0 ? "url(#positive-grad)" : "url(#negative-grad)";
}
